import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useCookies } from 'react-cookie';
import { BUTTON_VARIANT, H2 } from '@manulife/mux';
import {
  ErrorAlert,
  Form,
  FormattedRichMessage,
  CIAMLink,
  CIAMLinkButton,
  PasswordInput,
  PASSWORD_INPUT_VARIANTS,
  UsernameInput,
  USERNAME_VARIANTS,
  ErrorShape,
  useRealm,
  useRichIntl,
  CoBrandingButton,
  customStyles,
  useRedesignContext,
  sendAdobeAnalyticsLogin,
} from 'ciam-self-service-shared';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import InternetExplorerUserWarning from '../../common/InternetExplorerUserWarning';
import FormElement from '../../common/FormElement';
import HideWhenEmbedded from '../../Embedded/HideWhenEmbedded';
import AnalyticsDataLayer from '../../common/Analytics';
import { isEmbeddedSelector, goToEmbeddedSelector } from '../../Embedded/selectors';
import {
  REMEMBER_USERNAME_COOKIE_NAME,
  REMEMBER_USERNAME_COOKIE_EXPIRATION_DAYS,
  EVENT_SIGNIN_START,
  SIGNIN_PAGE_NAME,
  EVENT_SIGNIN_ERROR,
} from '../constants';
import { Wrapper } from './styledComponents';
import AemNotification from '../../AemNotification';
import { FormattedMessage } from 'react-intl';

function SignInForm(props) {
  const realm = useRealm();
  const { onLogin, loginLoadingStatus, error } = props;
  const [cookies, setCookie, removeCookie] = useCookies([`${REMEMBER_USERNAME_COOKIE_NAME}-${realm}`]);
  const [username, setUsername] = useState(cookies[`${REMEMBER_USERNAME_COOKIE_NAME}-${realm}`] || '');
  const [password, setPassword] = useState('');
  const [rememberUsername, setRememberUsername] = useState(
    Boolean(cookies[`${REMEMBER_USERNAME_COOKIE_NAME}-${realm}`]),
  );
  const embedded = useSelector(isEmbeddedSelector);
  const gotoEmbedded = useSelector(goToEmbeddedSelector);
  const isEmbedded = embedded || gotoEmbedded;
  const location = useLocation();
  const isVerified = location?.state?.isVerified;
  const richIntl = useRichIntl();
  const { showRedesignedView } = useRedesignContext();

  const handleLogin = useCallback(
    (event) => {
      // eslint-disable-next-line no-unused-expressions
      event?.preventDefault();
      if (rememberUsername) {
        setCookie(`${REMEMBER_USERNAME_COOKIE_NAME}-${realm}`, username, {
          path: '/',
          secure: true,
          httpOnly: false,
          sameSite: 'lax',
          expires: dayjs().add(REMEMBER_USERNAME_COOKIE_EXPIRATION_DAYS, 'days').toDate(),
        });
      }
      onLogin(username, password);
    },
    [onLogin, username, password, rememberUsername, setCookie, realm],
  );

  const handleRememberUsernameChanged = useCallback(
    (newRememberUsername) => {
      if (!newRememberUsername) {
        removeCookie(`${REMEMBER_USERNAME_COOKIE_NAME}-${realm}`);
      }
      setRememberUsername(newRememberUsername);
    },
    [realm, removeCookie],
  );

  const sendLoginDataToAdobe = useCallback((type, error) => {
    const analyticsData = {
      login: {
        method: 'standard',
      },
      ...(error && {
        error: {
          errorCode: error?.errorCode,
          errorType: error?.errorType,
          errorDescription: error?.description,
        },
      }),
    };
    sendAdobeAnalyticsLogin(type, analyticsData);
  }, []);

  useEffect(() => {
    sendLoginDataToAdobe('START', null);
  }, [sendLoginDataToAdobe]);

  useEffect(() => {
    if (error?.errorCode) {
      sendLoginDataToAdobe('ERROR', error);
    }
  }, [sendLoginDataToAdobe, error]);

  return (
    <>
      <Form onSubmit={handleLogin}>
        {error?.eventType === EVENT_SIGNIN_ERROR && (
          <AnalyticsDataLayer eventName={EVENT_SIGNIN_ERROR} error={error} ignoreSession />
        )}
        {!error && <AnalyticsDataLayer pageName={SIGNIN_PAGE_NAME} eventName={EVENT_SIGNIN_START} ignoreSession />}
        <Wrapper>
          <InternetExplorerUserWarning />
        </Wrapper>
        <AemNotification displayPage="signIn" />
        <ErrorAlert error={error} callouts />
        {isVerified && (
          <Wrapper>
            <FormattedMessage id="signin.page.firstLogin.instruction" />
          </Wrapper>
        )}
        <FormElement>
          <UsernameInput
            variant={USERNAME_VARIANTS.LOGIN}
            id="username"
            value={username}
            onChange={setUsername}
            rememberUsername={rememberUsername}
            onRememberUsernameChange={handleRememberUsernameChanged}
            required
            ariaRequired
            isEmbedded={isEmbedded}
          />
        </FormElement>
        <FormElement>
          <PasswordInput
            variant={PASSWORD_INPUT_VARIANTS.LOGIN}
            id="password"
            value={password}
            onChange={setPassword}
            required
            ariaRequired
          />
        </FormElement>
        <FormElement spacing={showRedesignedView ? 1 : 2}>
          <CoBrandingButton
            type="submit"
            variant={BUTTON_VARIANT.PRIMARY}
            saving={loginLoadingStatus}
            disabled={loginLoadingStatus}
            customStyle={customStyles.button}
            data-testid="submit"
            analyticsData={{
              click: {
                linkName: richIntl.formatMessage({ id: 'common.signin' }),
                linkHref: '/',
              },
            }}
          >
            <FormattedRichMessage id="common.signin" />
          </CoBrandingButton>
        </FormElement>
      </Form>

      <HideWhenEmbedded>
        {!isVerified && (
          <>
            {showRedesignedView ? (
              <FormElement data-testid="registration">
                {richIntl.formatMessage({ id: 'signin.page.setUpManulifeId' })}
              </FormElement>
            ) : (
              <>
                <H2>
                  <FormattedRichMessage id="signin.noIdPrompt" />
                </H2>
                <FormElement data-testid="registration">
                  <CIAMLinkButton
                    to="/register"
                    variant={BUTTON_VARIANT.SECONDARY}
                    customStyle={customStyles.button}
                    analyticsData={{
                      click: {
                        linkName: richIntl.formatMessage({ id: 'signin.setUpManulifeId' }),
                        linkHref: '/register',
                      },
                    }}
                  >
                    <FormattedRichMessage id="signin.setUpManulifeId" />
                  </CIAMLinkButton>
                </FormElement>
              </>
            )}

            <FormElement>
              <CIAMLink
                to="/aboutmanulifeid"
                analyticsData={{
                  click: {
                    linkName: richIntl.formatMessage({ id: 'signin.whatIsManulifeId' }),
                    linkHref: '/aboutmanulifeid',
                  },
                }}
              >
                {richIntl.formatMessage({ id: 'signin.whatIsManulifeId' })}
              </CIAMLink>
            </FormElement>
          </>
        )}
      </HideWhenEmbedded>
    </>
  );
}

SignInForm.propTypes = {
  onLogin: PropTypes.func.isRequired,
  loginLoadingStatus: PropTypes.bool.isRequired,
  error: ErrorShape,
};

SignInForm.defaultProps = {
  error: null,
};

export default SignInForm;
